import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  AllocateQuotaPayload,
  AllocateQuotaResponse,
  ClientQuotaDetail,
  ClientQuotaListRow,
  ClientQuotaTableDto,
  QuotaAllocationDetail,
  QuotaAllocationListRow,
  QuotaAllocationRequestDetail,
  QuotaAllocationRequestListRow,
  QuotaAllocationRequestTableDto,
  QuotaAllocationTableDto,
} from '../../models/card-quota/client-quota.model';

@Injectable({ providedIn: 'root' })
export class MapperClientQuotaService {
  mapTable(dto: ClientQuotaTableDto): {
    items: ClientQuotaListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as ClientQuotaTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as ClientQuotaListRow[];
    return {
      items: rows.map((r) => this.mapListRow(r as ClientQuotaListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: ClientQuotaListRow & Record<string, unknown>): ClientQuotaListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      siteId: row.siteId != null ? Number(row.siteId ?? row['SiteId']) : null,
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      quotaLevel: String(row.quotaLevel ?? row['QuotaLevel'] ?? ''),
      quotaStatus: String(row.quotaStatus ?? row['QuotaStatus'] ?? ''),
      cardScope: String(row.cardScope ?? row['CardScope'] ?? ''),
      quotaApproved: Number(row.quotaApproved ?? row['QuotaApproved'] ?? 0),
      quotaUsed: Number(row.quotaUsed ?? row['QuotaUsed'] ?? 0),
      quotaAllocated: Number(row.quotaAllocated ?? row['QuotaAllocated'] ?? 0),
      quotaRemaining: Number(row.quotaRemaining ?? row['QuotaRemaining'] ?? 0),
      parentQuotaId: row.parentQuotaId != null ? Number(row.parentQuotaId ?? row['ParentQuotaId']) : null,
      validFrom: row.validFrom != null ? String(row.validFrom ?? row['ValidFrom']) : null,
      validTo: row.validTo != null ? String(row.validTo ?? row['ValidTo']) : null,
    };
  }

  mapDetail(dto: ClientQuotaDetail & Record<string, unknown>): ClientQuotaDetail {
    const base = this.mapListRow(dto);
    const allocations = (dto.recentAllocations ?? dto['RecentAllocations'] ?? []) as unknown as Record<string, unknown>[];
    return {
      ...base,
      cardTypeId: dto.cardTypeId != null ? Number(dto.cardTypeId ?? dto['CardTypeId']) : null,
      cardTypeName: String(dto.cardTypeName ?? dto['CardTypeName'] ?? ''),
      subscriptionId: dto.subscriptionId != null ? Number(dto.subscriptionId ?? dto['SubscriptionId']) : null,
      subscriptionName: String(dto.subscriptionName ?? dto['SubscriptionName'] ?? ''),
      allowSelfManage: Boolean(dto.allowSelfManage ?? dto['AllowSelfManage'] ?? false),
      allowActivateCode: Boolean(dto.allowActivateCode ?? dto['AllowActivateCode'] ?? false),
      approvalRequestId:
        dto.approvalRequestId != null ? Number(dto.approvalRequestId ?? dto['ApprovalRequestId']) : null,
      recentAllocations: allocations.map((a) => ({
        id: Number(a['id'] ?? a['Id'] ?? 0),
        tenantClientId: Number(a['tenantClientId'] ?? a['TenantClientId'] ?? 0),
        tenantClientName: String(a['tenantClientName'] ?? a['TenantClientName'] ?? ''),
        allocatedQty: Number(a['allocatedQty'] ?? a['AllocatedQty'] ?? 0),
        action: String(a['action'] ?? a['Action'] ?? ''),
        createDate: (a['createDate'] ?? a['CreateDate'] ?? null) as string | null,
      })),
    };
  }

  mapAllocatePayload(
    data: {
      masterQuotaId: number;
      tenantClientId: number;
      siteId: number;
      allocatedQty: number;
      note?: string;
      submitForApproval?: boolean;
    },
    userId: number,
    menuId: number
  ): AllocateQuotaPayload {
    return {
      userId,
      menuId,
      menuListId: menuId,
      masterQuotaId: data.masterQuotaId,
      tenantClientId: data.tenantClientId,
      siteId: data.siteId,
      allocatedQty: data.allocatedQty,
      action: 'allocate',
      note: data.note ?? null,
      submitForApproval: data.submitForApproval ?? true,
    };
  }

  mapAllocateResponse(res: unknown): AllocateQuotaResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      idReturn: Number(raw['idReturn'] ?? raw['IdReturn'] ?? 0),
      requiresApproval: Boolean(raw['requiresApproval'] ?? raw['RequiresApproval'] ?? false),
      approvalRequestId:
        raw['approvalRequestId'] != null || raw['ApprovalRequestId'] != null
          ? Number(raw['approvalRequestId'] ?? raw['ApprovalRequestId'])
          : null,
      allocationRequestId:
        raw['allocationRequestId'] != null || raw['AllocationRequestId'] != null
          ? Number(raw['allocationRequestId'] ?? raw['AllocationRequestId'])
          : null,
      allocationId:
        raw['allocationId'] != null || raw['AllocationId'] != null
          ? Number(raw['allocationId'] ?? raw['AllocationId'])
          : null,
    };
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }

  quotaLevelLabel(level: string): string {
    return level === 'sub' ? 'ร้านค้า (โควต้าย่อย)' : 'ฝ่ายอาคาร (โควต้าหลัก)';
  }

  quotaStatusLabel(status: string): string {
    const map: Record<string, string> = {
      active: 'ใช้งาน',
      suspended: 'ระงับ',
      draft: 'Draft',
      pending: 'รออนุมัติ',
      expired: 'หมดอายุ',
      cancelled: 'ยกเลิก',
    };
    return map[status] ?? status;
  }

  mapAllocationTable(dto: QuotaAllocationTableDto): {
    items: QuotaAllocationListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as QuotaAllocationTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as QuotaAllocationListRow[];
    return {
      items: rows.map((r) => this.mapAllocationRow(r as QuotaAllocationListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapAllocationRow(row: QuotaAllocationListRow & Record<string, unknown>): QuotaAllocationListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      masterQuotaId: Number(row.masterQuotaId ?? row['MasterQuotaId'] ?? 0),
      masterQuotaName: String(row.masterQuotaName ?? row['MasterQuotaName'] ?? ''),
      tenantClientId: Number(row.tenantClientId ?? row['TenantClientId'] ?? 0),
      tenantClientName: String(row.tenantClientName ?? row['TenantClientName'] ?? ''),
      allocatedQty: Number(row.allocatedQty ?? row['AllocatedQty'] ?? 0),
      action: String(row.action ?? row['Action'] ?? ''),
      approvalRequestId:
        row.approvalRequestId != null || row['ApprovalRequestId'] != null
          ? Number(row.approvalRequestId ?? row['ApprovalRequestId'])
          : null,
      createDate: row.createDate != null ? String(row.createDate ?? row['CreateDate']) : null,
    };
  }

  mapAllocationDetail(dto: QuotaAllocationDetail & Record<string, unknown>): QuotaAllocationDetail {
    return {
      id: Number(dto.id ?? dto['Id'] ?? 0),
      name: String(dto.name ?? dto['Name'] ?? ''),
      masterQuotaId: Number(dto.masterQuotaId ?? dto['MasterQuotaId'] ?? 0),
      masterQuotaName: String(dto.masterQuotaName ?? dto['MasterQuotaName'] ?? ''),
      subQuotaId: dto.subQuotaId != null ? Number(dto.subQuotaId ?? dto['SubQuotaId']) : null,
      tenantClientId: Number(dto.tenantClientId ?? dto['TenantClientId'] ?? 0),
      tenantClientName: String(dto.tenantClientName ?? dto['TenantClientName'] ?? ''),
      siteId: Number(dto.siteId ?? dto['SiteId'] ?? 0),
      siteName: String(dto.siteName ?? dto['SiteName'] ?? ''),
      allocatedQty: Number(dto.allocatedQty ?? dto['AllocatedQty'] ?? 0),
      action: String(dto.action ?? dto['Action'] ?? ''),
      approvalRequestId:
        dto.approvalRequestId != null || dto['ApprovalRequestId'] != null
          ? Number(dto.approvalRequestId ?? dto['ApprovalRequestId'])
          : null,
      createDate: dto.createDate != null ? String(dto.createDate ?? dto['CreateDate']) : null,
    };
  }

  mapAllocationRequestTable(dto: QuotaAllocationRequestTableDto): {
    items: QuotaAllocationRequestListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as QuotaAllocationRequestTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as QuotaAllocationRequestListRow[];
    return {
      items: rows.map((r) =>
        this.mapAllocationRequestRow(r as QuotaAllocationRequestListRow & Record<string, unknown>)
      ),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapAllocationRequestRow(
    row: QuotaAllocationRequestListRow & Record<string, unknown>
  ): QuotaAllocationRequestListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      masterQuotaId: Number(row.masterQuotaId ?? row['MasterQuotaId'] ?? 0),
      masterQuotaName: String(row.masterQuotaName ?? row['MasterQuotaName'] ?? ''),
      tenantClientId: Number(row.tenantClientId ?? row['TenantClientId'] ?? 0),
      tenantClientName: String(row.tenantClientName ?? row['TenantClientName'] ?? ''),
      requestedQty: Number(row.requestedQty ?? row['RequestedQty'] ?? 0),
      approvedQty: row.approvedQty != null ? Number(row.approvedQty ?? row['ApprovedQty']) : null,
      requestType: String(row.requestType ?? row['RequestType'] ?? ''),
      status: String(row.status ?? row['Status'] ?? ''),
      approvalRequestId:
        row.approvalRequestId != null ? Number(row.approvalRequestId ?? row['ApprovalRequestId']) : null,
      allocationId: row.allocationId != null ? Number(row.allocationId ?? row['AllocationId']) : null,
      createDate: row.createDate != null ? String(row.createDate ?? row['CreateDate']) : null,
    };
  }

  mapAllocationRequestDetail(
    dto: QuotaAllocationRequestDetail & Record<string, unknown>
  ): QuotaAllocationRequestDetail {
    return {
      id: Number(dto.id ?? dto['Id'] ?? 0),
      name: String(dto.name ?? dto['Name'] ?? ''),
      masterQuotaId: Number(dto.masterQuotaId ?? dto['MasterQuotaId'] ?? 0),
      masterQuotaName: String(dto.masterQuotaName ?? dto['MasterQuotaName'] ?? ''),
      tenantClientId: Number(dto.tenantClientId ?? dto['TenantClientId'] ?? 0),
      tenantClientName: String(dto.tenantClientName ?? dto['TenantClientName'] ?? ''),
      siteId: Number(dto.siteId ?? dto['SiteId'] ?? 0),
      siteName: String(dto.siteName ?? dto['SiteName'] ?? ''),
      requestedQty: Number(dto.requestedQty ?? dto['RequestedQty'] ?? 0),
      approvedQty: dto.approvedQty != null ? Number(dto.approvedQty ?? dto['ApprovedQty']) : null,
      requestType: String(dto.requestType ?? dto['RequestType'] ?? ''),
      status: String(dto.status ?? dto['Status'] ?? ''),
      approvalRequestId:
        dto.approvalRequestId != null ? Number(dto.approvalRequestId ?? dto['ApprovalRequestId']) : null,
      allocationId: dto.allocationId != null ? Number(dto.allocationId ?? dto['AllocationId']) : null,
      subQuotaId: dto.subQuotaId != null ? Number(dto.subQuotaId ?? dto['SubQuotaId']) : null,
      note: dto.note != null ? String(dto.note ?? dto['Note']) : null,
      createDate: dto.createDate != null ? String(dto.createDate ?? dto['CreateDate']) : null,
    };
  }

  allocationRequestStatusLabel(status: string): string {
    const map: Record<string, string> = {
      draft: 'Draft',
      pending: 'รออนุมัติ',
      approved: 'อนุมัติแล้ว',
      rejected: 'ปฏิเสธ',
      cancelled: 'ยกเลิก',
    };
    return map[status] ?? status;
  }

  allocationActionLabel(action: string): string {
    const map: Record<string, string> = {
      pending: 'รออนุมัติ',
      allocate: 'จัดสรร',
      increase: 'เพิ่ม',
      decrease: 'ลด',
      revoke: 'คืนโควต้า',
      rejected: 'ปฏิเสธ',
      cancelled: 'ยกเลิก',
    };
    return map[action] ?? action;
  }
}
